/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type translations from '@/locales/en-GB/translations.json';

// Derive country codes from translations.json
export type CountryCode = keyof typeof translations.countries;

// Derive state codes from translations.json
type USStateCode = keyof typeof translations.countries.US.states;
type CAStateCode = keyof typeof translations.countries.CA.states;

// Type for state/province code based on country
export type StateCode<T extends CountryCode> = T extends 'US' ? USStateCode : T extends 'CA' ? CAStateCode : never;

// List of supported country codes (names are retrieved from i18next at runtime)
// Only the United States is currently supported in the storefront UI. Canada is intentionally
// excluded from the country selector even though the underlying type/translations retain it,
// so existing customer records with countryCode === 'CA' continue to be handled gracefully.
export const COUNTRY_CODES: readonly CountryCode[] = ['US'] as const;

/** Default country code for address forms */
export const DEFAULT_COUNTRY_CODE: CountryCode = 'US';

// US Postal Code validation (5 digits or 5+4 format)
export const usPostalCodeRegex = /^\d{5}(-\d{4})?$/;

// Canadian Postal Code validation (A1A 1A1 format)
export const canadianPostalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

// ---------------------------------------------------------------------------
// Name validation primitives
// ---------------------------------------------------------------------------
//
// First name and last name on the shipping/billing address forms accept
// `^[a-zA-Z]+$` only — no spaces, hyphens, apostrophes, numbers, or other
// special characters.
//
// Legacy parity:
//   `site_ashleyhomestore_kiosk/cartridge/forms/default/shippingaddress.xml`
//     <field firstName regexp="^[a-zA-Z]+$" max-length="50"
//            parse-error="validate.lettersOnly" .../>
//     <field lastName  regexp="^[a-zA-Z]+$" max-length="50"
//            parse-error="validate.lettersOnly" .../>
//
// The main brand cartridge (`org_ashcomm/.../shippingaddress.xml`) had a
// looser regex that permitted hyphens / spaces / accented chars on first and
// last names. The Ashley merchandiser explicitly chose the **kiosk-strict**
// variant for the React storefront (rejects "O'Brien", "Anne-Marie", "José",
// etc. — surfaces a translated error) over the more permissive
// `org_ashcomm` variant. Confirm with the merchandiser before broadening.
//
// `<Input maxLength={NAME_MAX_LENGTH}>` on the field caps keystrokes/paste
// at 50 chars so the regex/length check never has to fire for trivial
// over-typing. Submit-time validation still runs server-side via
// `createShippingAddressSchema(t).safeParse(...)`.
export const NAME_REGEX = /^[a-zA-Z]+$/;
export const NAME_MAX_LENGTH = 50;

// ---------------------------------------------------------------------------
// City validation primitives
// ---------------------------------------------------------------------------
//
// City accepts letters (incl. accented Latin in the U+00C0..U+024F range),
// spaces, hyphens, apostrophes, and periods. The first character must be a
// letter so values starting with whitespace or punctuation are rejected up
// front. Real US cities such as `St. Louis`, `Winston-Salem`, `O'Fallon`,
// and `San José` continue to validate.
//
// Legacy parity:
//   `org_ashcomm/cartridge/forms/default/shippingaddress.xml` only enforced
//   `min-length="2" max-length="50"` with no `regexp`, so this is a
//   stricter rule layered on top to reject digits and `@#$%&*` etc.
export const CITY_REGEX = /^[A-Za-z\u00C0-\u024F][A-Za-z\u00C0-\u024F\s\-'.]*$/;
export const CITY_MIN_LENGTH = 2;
export const CITY_MAX_LENGTH = 50;
